import { type FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Icon } from '@packages/ui/shared';
import { colors } from '@packages/core/styles';
import type { Publicationtype } from './stis-publications';
import type { CarlineOption, StisFilterValues } from './stis-inline-filters';
import { PublicationType } from './publication-type-icons';
import { StisFilterDropdown } from './stis-filter-dropdown';
import { useVehicleFilterOptions } from './hooks/use-vehicle-filter-options';
import {
    buildFilterChips,
    countAppliedFilters,
    formatPublishedMonth,
    hasTechTips,
    PUBLISHED_MONTHS,
    publishedYearOptions,
    type FilterChip,
    type VinError,
} from './vehicle-filter.utils';
import { DocumentCodeMenu } from './document-code-menu';
import { useDocumentCodeSuggestions, type DocumentCodeScope } from './hooks/use-document-code-suggestions';

type Props = {
    publicationTypes: Publicationtype[];
    value: Partial<StisFilterValues>;
    appliedFilters: Partial<StisFilterValues>;
    showVehicleFilters: boolean;
    sessionVin?: string;
    isSignedIn: boolean;
    fallbackModelYears: (string | number)[];
    fallbackCarlines: CarlineOption[];
    collapsed: boolean;
    resultCount?: number;
    vinError?: VinError | null;
    showAccessoryPartNumber?: boolean;
    partNumber?: string;
    partNumberScope?: DocumentCodeScope;
    onPartNumberChange?: (value: string) => void;
    onCollapsedChange: (collapsed: boolean) => void;
    onChange: (values: StisFilterValues) => void;
    onApply: (values: StisFilterValues) => void;
    onCollapse: () => void;
    onSubmit: () => void;
};

const fieldLabel = {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: colors.textDarkSecondary,
} as const;

const linkButtonDisabled = {
    color: colors.grayFive,
    cursor: 'not-allowed',
    '&:hover': {
        color: colors.grayFive,
        textDecoration: 'none',
    },
} as const;

const linkButton = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 2px',
    border: 'none',
    background: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: colors.blueOne,
    cursor: 'pointer',
    '&:hover': {
        color: colors.blueOneActive,
        textDecoration: 'underline',
    },
    '&:disabled': linkButtonDisabled,
} as const;

const SKELETON_TYPE_ROWS = 10;
const COLLAPSE_MS = 220;
const SWAP_MS = 160;

const useStyles = createUseStyles({
    panel: {
        padding: '12px 20px',
        marginBottom: 16,
        backgroundColor: colors.grayTwo,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '6px 12px',
        minHeight: 40,
    },
    collapsible: {
        display: 'grid',
        gridTemplateRows: '1fr',
        visibility: 'visible',
        transition: `grid-template-rows ${COLLAPSE_MS}ms ease, visibility 0s linear 0s`,
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
        },
    },
    collapsibleClosed: {
        gridTemplateRows: '0fr',
        visibility: 'hidden',
        transition: `grid-template-rows ${COLLAPSE_MS}ms ease, visibility 0s linear ${COLLAPSE_MS}ms`,
    },
    collapsibleInner: {
        minHeight: 0,
        overflow: 'hidden',
    },
    collapsibleInnerSettled: {
        overflow: 'visible',
    },
    bodyContent: {
        padding: '4px 0 12px',
    },
    headerActionStart: linkButton,
    linkButton,
    body: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 440px)',
        gap: 34,
        '@media (max-width: 1100px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 24,
        },
    },
    types: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    typesHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    badge: {
        padding: '2px 8px',
        borderRadius: 10,
        backgroundColor: colors.blueFour,
        color: colors.blueOneActive,
        fontSize: 12,
        fontWeight: 700,
    },
    typeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateRows: ({ typeRows }: { typeRows: number }) => `repeat(${typeRows}, auto)`,
        gridAutoFlow: 'column',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 3,
        '@media (max-width: 700px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'none',
            gridAutoFlow: 'row',
        },
    },
    typeRowLocked: {
        cursor: 'not-allowed',
        opacity: 0.45,
    },
    lockedHint: {
        position: 'absolute',
        left: 14,
        bottom: 'calc(100% - 4px)',
        zIndex: 30,
        padding: '5px 9px',
        borderRadius: 3,
        backgroundColor: colors.black,
        color: colors.white,
        fontSize: 12,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        opacity: 1,
    },
    checkboxLocked: {
        pointerEvents: 'none',
    },
    typeRow: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0,
        height: 40,
        margin: 0,
        padding: '0 14px',
        borderBottom: `1px solid ${colors.grayTwo}`,
        fontSize: 15,
        fontWeight: 400,
        color: colors.textDarkPrimary,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: colors.grayOne,
        },
    },
    typeRowSelected: {
        backgroundColor: '#f4f8fc',
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
    },
    skeletonBar: {
        width: '55%',
        height: 12,
        borderRadius: 6,
        backgroundColor: colors.grayTwo,
    },
    checkbox: {
        flex: '0 0 auto',
        width: 16,
        height: 16,
        margin: 0,
        accentColor: colors.blueOne,
        cursor: 'pointer',
    },
    typeIcon: {
        flex: '0 0 auto',
        display: 'flex',
    },
    typeName: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    swap: {
        display: 'grid',
    },
    swapPane: {
        gridArea: '1 / 1',
        opacity: 1,
        visibility: 'visible',
        transition: `opacity ${SWAP_MS}ms ease, visibility 0s linear 0s`,
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
        },
    },
    partNumberBox: {
        position: 'relative',
        '& input': {
            width: '100%',
        },
    },
    swapPaneHidden: {
        opacity: 0,
        visibility: 'hidden',
        pointerEvents: 'none',
        transition: `opacity ${SWAP_MS}ms ease, visibility 0s linear ${SWAP_MS}ms`,
    },
    lockedReasonText: {
        position: 'absolute',
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
    techTipsNote: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 4,
        backgroundColor: colors.blueFour,
        border: `1px solid ${colors.blueThree}`,
        fontSize: 14,
        lineHeight: '20px',
        color: colors.textDarkPrimary,
        '& > :first-child': {
            flex: '0 0 auto',
            marginTop: 1,
        },
    },
    vehicle: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        paddingLeft: 34,
        borderLeft: `1px solid ${colors.grayThree}`,
        '@media (max-width: 1100px)': {
            paddingLeft: 0,
            borderLeft: 'none',
        },
    },
    vehicleHeading: {
        ...fieldLabel,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    fieldLabel,
    vinRow: {
        position: 'relative',
        display: 'flex',
    },
    vinInput: {
        flex: 1,
        minWidth: 0,
        height: 40,
        padding: '0 40px 0 12px',
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 3,
        fontSize: 17,
        color: colors.textDarkPrimary,
        letterSpacing: '0.02em',
        '&:focus': {
            outline: 'none',
            border: `2px solid ${colors.blueOne}`,
            padding: '0 39px 0 11px',
        },
    },
    vinInputDisabled: {
        '&:disabled': {
            backgroundColor: colors.grayOne,
            borderColor: colors.grayThree,
            cursor: 'not-allowed',
        },
    },
    vinInputInvalid: {
        borderColor: colors.redOne,
    },
    vinClear: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
    },
    vinError: {
        fontSize: 13,
        color: colors.redOneActive,
    },
    vinNote: {
        fontSize: 13,
        color: colors.textDarkSecondary,
    },
    divider: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: colors.grayFive,
        '&::before, &::after': {
            content: '""',
            flex: 1,
            height: 1,
            backgroundColor: colors.grayThree,
        },
    },
    filtersButton: {
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 36,
        padding: '0 12px',
        border: `1px solid ${colors.blueOne}`,
        borderRadius: 3,
        backgroundColor: colors.white,
        color: colors.blueOne,
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
    },
    filtersCount: {
        padding: '1px 7px',
        borderRadius: 9,
        backgroundColor: colors.blueOne,
        color: colors.white,
        fontSize: 12,
        fontWeight: 700,
    },
    barDivider: {
        width: 1,
        height: 24,
        backgroundColor: colors.grayThree,
    },
    chipWrap: {
        position: 'relative',
        display: 'inline-flex',
    },
    typeList: {
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 20,
        width: '100%',
        boxSizing: 'border-box',
        maxHeight: 264,
        overflowY: 'auto',
        paddingTop: 6,
    },
    typeListInner: {
        padding: 4,
        border: `1px solid ${colors.blueThree}`,
        borderRadius: 8,
        backgroundColor: colors.blueFour,
        boxShadow: '0 6px 18px rgba(7, 20, 36, 0.18)',
    },
    typeListRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 4px 4px 8px',
        borderRadius: 12,
        fontSize: 14,
        color: colors.blueOneActive,
        '&:hover': {
            backgroundColor: colors.blueThree,
        },
    },
    typeListName: {
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        maxWidth: 320,
        padding: '3px 4px 3px 10px',
        border: `1px solid ${colors.blueThree}`,
        borderRadius: 15,
        backgroundColor: colors.blueFour,
        color: colors.blueOneActive,
        fontSize: 14,
    },
    chipGroup: {
        flex: '0 0 auto',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        color: colors.textDarkSecondary,
    },
    chipExtra: {
        flex: '0 0 auto',
        border: 'none',
        cursor: 'pointer',
        padding: '0 6px',
        borderRadius: 10,
        backgroundColor: colors.blueThree,
        fontSize: 12,
        fontWeight: 700,
        color: colors.blueOneActive,
    },
    chipLabel: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chipRemove: {
        flex: '0 0 auto',
        display: 'flex',
        padding: 2,
        border: 'none',
        borderRadius: '50%',
        background: 'none',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: colors.blueThree,
        },
    },
    barEmpty: {
        fontSize: 14,
        color: colors.textDarkSecondary,
    },
    barCount: {
        flex: '0 0 auto',
        marginLeft: 'auto',
        fontSize: 14,
        color: colors.textDarkSecondary,
        whiteSpace: 'nowrap',
        '& strong': {
            color: colors.textDarkPrimary,
        },
    },
});

const toValues = (filters: Partial<StisFilterValues>): StisFilterValues => ({
    publicationTypes: filters.publicationTypes ?? [],
    carline: filters.carline ?? [],
    modelYear: filters.modelYear ? String(filters.modelYear) : undefined,
    trim: filters.trim,
    vin: filters.vin,
    publishedYear: filters.publishedYear ? String(filters.publishedYear) : undefined,
    publishedMonth: filters.publishedMonth ? String(filters.publishedMonth) : undefined,
});

export const StisSearchCriteria: FC<Props> = ({
    publicationTypes,
    value,
    appliedFilters,
    showVehicleFilters,
    sessionVin,
    isSignedIn,
    fallbackModelYears,
    fallbackCarlines,
    collapsed,
    resultCount,
    vinError,
    showAccessoryPartNumber = false,
    partNumber = '',
    partNumberScope = {},
    onPartNumberChange,
    onCollapsedChange,
    onChange,
    onApply,
    onCollapse,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const classes = useStyles({
        typeRows: publicationTypes.length > 0 ? Math.ceil(publicationTypes.length / 2) : SKELETON_TYPE_ROWS,
    });
    const bodyId = useId();
    const [isSettledOpen, setIsSettledOpen] = useState(!collapsed);

    useEffect(() => {
        if (collapsed) {
            setIsSettledOpen(false);
            return;
        }
        const timer = window.setTimeout(() => setIsSettledOpen(true), COLLAPSE_MS);
        return () => window.clearTimeout(timer);
    }, [collapsed]);

    const current = useMemo(() => toValues(value), [value]);
    const applied = useMemo(() => toValues(appliedFilters), [appliedFilters]);

    const emit = (changes: Partial<StisFilterValues>) => onChange({ ...current, ...changes });

    const selectedCarlineId = current.carline[0]?.id;
    const { modelYears, carlines, trims, supportsTrim } = useVehicleFilterOptions({
        isSignedIn,
        modelYear: current.modelYear,
        carlineId: selectedCarlineId,
        fallbackModelYears,
        fallbackCarlines,
    });

    const sortedTypes = useMemo(
        () => [...publicationTypes].sort((a, b) => a.publicationType.localeCompare(b.publicationType)),
        [publicationTypes]
    );
    const selectedTypeNames = useMemo(
        () => new Set(current.publicationTypes.map((type) => type.publicationType)),
        [current.publicationTypes]
    );

    const chips = useMemo(() => buildFilterChips(applied), [applied]);
    const appliedCount = useMemo(() => countAppliedFilters(applied), [applied]);
    const [isTypeListOpen, setIsTypeListOpen] = useState(false);
    const [hintedType, setHintedType] = useState<string | null>(null);
    const typeListCloseTimer = useRef<number>();

    const openTypeList = () => {
        window.clearTimeout(typeListCloseTimer.current);
        setIsTypeListOpen(true);
    };

    const closeTypeListSoon = () => {
        window.clearTimeout(typeListCloseTimer.current);
        typeListCloseTimer.current = window.setTimeout(() => setIsTypeListOpen(false), 180);
    };

    useEffect(() => () => window.clearTimeout(typeListCloseTimer.current), []);

    const toggleType = (type: Publicationtype) => {
        const wasSelected = selectedTypeNames.has(type.publicationType);
        const publicationTypes = wasSelected
            ? current.publicationTypes.filter((selected) => selected.publicationType !== type.publicationType)
            : [...current.publicationTypes, type];
        emit(
            wasSelected && hasTechTips([type])
                ? { publicationTypes, publishedYear: undefined, publishedMonth: undefined }
                : { publicationTypes }
        );
    };

    const changeModelYear = (modelYear?: string) =>
        emit(isSignedIn ? { modelYear, carline: [], trim: undefined } : { modelYear });

    const changeCarline = (carlineId?: string) => {
        const match = carlines.find((option) => option.id === carlineId);
        emit({ carline: match ? [match] : [], trim: undefined });
    };

    const changeTrim = (vehicleId?: string) => emit({ trim: trims.find((option) => option.vehicleId === vehicleId) });

    const vinText = current.vin ?? '';
    const hasVin = vinText.trim().length > 0;
    const hasVehiclePick = Boolean(current.modelYear || current.carline.length > 0 || current.trim);
    const vinDisabled = !hasVin && hasVehiclePick;

    const clearVin = () => emit({ vin: undefined });

    const vinErrorMessage: Record<VinError, string> = {
        format: t(
            'publications:searchCriteria.vinFormat',
            'Enter all 17 characters of the VIN. VINs never use I, O or Q.'
        ),
    };

    const removeChip = (chip: FilterChip) => {
        switch (chip.group) {
            case 'type':
                onApply({ ...applied, publicationTypes: [] });
                return;
            case 'vin':
                onApply({ ...applied, vin: undefined, modelYear: undefined, carline: [], trim: undefined });
                return;
            case 'year':
                onApply(
                    isSignedIn
                        ? { ...applied, modelYear: undefined, carline: [], trim: undefined }
                        : { ...applied, modelYear: undefined }
                );
                return;
            case 'carline':
                onApply({ ...applied, carline: [], trim: undefined });
                return;
            case 'trim':
                onApply({ ...applied, trim: undefined });
                return;
            case 'publishedYear':
                onApply({ ...applied, publishedYear: undefined });
                return;
            case 'publishedMonth':
                onApply({ ...applied, publishedMonth: undefined });
        }
    };

    const typeListLabel = t('publications:searchCriteria.typeList', 'The publication types you picked');

    const removeType = (publicationType: string) => {
        const remaining = applied.publicationTypes.filter((type) => type.publicationType !== publicationType);
        if (remaining.length < 2) {
            setIsTypeListOpen(false);
        }
        onApply({ ...applied, publicationTypes: remaining });
    };

    const chipGroupLabel: Record<FilterChip['group'], string> = {
        type: t('publications:searchCriteria.chipType', 'Type'),
        vin: t('publications:searchCriteria.chipVin', 'VIN'),
        year: t('publications:searchCriteria.chipYear', 'Year'),
        carline: t('publications:searchCriteria.chipCarline', 'Carline'),
        trim: t('publications:searchCriteria.chipTrim', 'Trim'),
        publishedYear: t('publications:searchCriteria.chipPublishedYear', 'Published year'),
        publishedMonth: t('publications:searchCriteria.chipPublishedMonth', 'Published month'),
    };

    const needsYearFirst = isSignedIn && !current.modelYear;
    const isTechTips = hasTechTips(current.publicationTypes);
    const hasOtherTypes = current.publicationTypes.some((type) => !hasTechTips([type]));
    // TechTIPS on its own is searched by published date. Mixed with anything else the search
    // falls back to keywords, so neither the date nor the vehicle applies.
    const isTechTipsOnly = isTechTips && !hasOtherTypes;
    const datesLockedByMix = isTechTips && hasOtherTypes;
    const datesLockedReason = datesLockedByMix
        ? t(
              'publications:searchCriteria.datesLockedByMix',
              'TechTIPS NewsLetter is picked with other types, so this search cannot be narrowed by published date'
          )
        : undefined;
    const [isPartMenuOpen, setIsPartMenuOpen] = useState(false);
    const partNumberBoxRef = useRef<HTMLDivElement>(null);
    const partNumbers = useDocumentCodeSuggestions({
        query: partNumber,
        scope: partNumberScope,
        enabled: isPartMenuOpen && showAccessoryPartNumber,
        browseWhenShort: true,
    });

    const hideVehiclePanes = isTechTips || showAccessoryPartNumber;
    const showRightColumn = isTechTips || showAccessoryPartNumber || showVehicleFilters || Boolean(sessionVin);
    const yearOptions = useMemo(() => publishedYearOptions(), []);

    return (
        <section className={classes.panel} aria-label={t('publications:searchCriteria.title', 'Narrow your search')}>
            <div id={bodyId} className={`${classes.collapsible} ${collapsed ? classes.collapsibleClosed : ''}`}>
                <div className={`${classes.collapsibleInner} ${isSettledOpen ? classes.collapsibleInnerSettled : ''}`}>
                    <div className={`${classes.bodyContent} ${showRightColumn ? classes.body : ''}`}>
                        <div className={classes.types} role="group" aria-labelledby="stis-criteria-types-label">
                            <div className={classes.typesHeader}>
                                <span id="stis-criteria-types-label" className={classes.fieldLabel}>
                                    {t('publications:quickFilters.publicationType', 'Publication Type')}
                                </span>
                                {current.publicationTypes.length > 0 && (
                                    <span className={classes.badge}>
                                        {t('publications:searchCriteria.selectedCount', '{{count}} selected', {
                                            count: current.publicationTypes.length,
                                        })}
                                    </span>
                                )}
                            </div>
                            <div className={classes.typeGrid} aria-busy={sortedTypes.length === 0}>
                                {sortedTypes.length === 0 &&
                                    Array.from({ length: SKELETON_TYPE_ROWS * 2 }, (_, index) => (
                                        <div key={index} className={classes.typeRow} aria-hidden="true">
                                            <span className={classes.skeletonBar} />
                                        </div>
                                    ))}
                                {sortedTypes.map((type) => {
                                    const isSelected = selectedTypeNames.has(type.publicationType);
                                    return (
                                        <label
                                            key={type.publicationType}
                                            className={`${classes.typeRow} ${
                                                isSelected ? classes.typeRowSelected : ''
                                            }`}
                                            title={type.publicationType}
                                        >
                                            <input
                                                type="checkbox"
                                                className={classes.checkbox}
                                                checked={isSelected}
                                                onChange={() => toggleType(type)}
                                            />
                                            <span className={classes.typeIcon}>
                                                <PublicationType publicationType={type.icon} size={18} />
                                            </span>
                                            <span className={classes.typeName}>{type.publicationType}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {showRightColumn && (
                            <div className={classes.swap}>
                                {showAccessoryPartNumber && (
                                    <div className={`${classes.vehicle} ${classes.swapPane}`}>
                                        <span className={classes.vehicleHeading}>
                                            <Icon name="car-repair" size={20} color="blueOne" />
                                            {t('publications:searchCriteria.accessory', 'Accessory')}
                                        </span>

                                        <div className={classes.field}>
                                            <label htmlFor="stis-criteria-part-number" className={classes.fieldLabel}>
                                                {t(
                                                    'publications:searchCriteria.accessoryPartNumber',
                                                    'Accessory part number'
                                                )}
                                            </label>
                                            <div
                                                ref={partNumberBoxRef}
                                                className={classes.partNumberBox}
                                                onBlur={(event) => {
                                                    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                                                        setIsPartMenuOpen(false);
                                                    }
                                                }}
                                            >
                                                <input
                                                    id="stis-criteria-part-number"
                                                    className={classes.vinInput}
                                                    value={partNumber}
                                                    autoComplete="off"
                                                    placeholder={t(
                                                        'publications:searchCriteria.partNumberPlaceholder',
                                                        'Start typing an accessory part number'
                                                    )}
                                                    onFocus={() => {
                                                        setIsPartMenuOpen(true);
                                                    }}
                                                    onChange={(event) => {
                                                        setIsPartMenuOpen(true);
                                                        onPartNumberChange?.(event.target.value);
                                                    }}
                                                />
                                                {isPartMenuOpen && (
                                                    <DocumentCodeMenu
                                                        options={partNumbers.suggestions}
                                                        keyboardTarget={partNumberBoxRef}
                                                        query={partNumber}
                                                        isLoading={partNumbers.isLoading}
                                                        hasMore={partNumbers.hasMore}
                                                        onReachEnd={partNumbers.loadMore}
                                                        moreDocumentsLabel={(count) =>
                                                            t(
                                                                'publications:searchCriteria.moreDocuments',
                                                                '+{{count}} more',
                                                                { count }
                                                            )
                                                        }
                                                        moreLabel={t(
                                                            'publications:searchCriteria.scrollForMore',
                                                            'Scroll for more'
                                                        )}
                                                        loadingLabel={t(
                                                            'publications:searchCriteria.loadingCodes',
                                                            'Loading codes'
                                                        )}
                                                        emptyLabel={t(
                                                            'publications:searchCriteria.noCodeMatches',
                                                            'No matches found'
                                                        )}
                                                        onSelect={(code) => {
                                                            onPartNumberChange?.(code);
                                                            setIsPartMenuOpen(false);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div
                                    className={`${classes.vehicle} ${classes.swapPane} ${
                                        isTechTips ? '' : classes.swapPaneHidden
                                    }`}
                                    aria-hidden={!isTechTips}
                                >
                                    <div className={classes.techTipsNote} role="note">
                                        <Icon name="info-solid" size={18} color="blueOne" />
                                        <span>
                                            {datesLockedReason ??
                                                t(
                                                    'publications:searchCriteria.techTipsNote',
                                                    'TechTIPS NewsLetter is searched by published year and month only. Uncheck it to filter by vehicle.'
                                                )}
                                        </span>
                                    </div>

                                    <div className={classes.field}>
                                        <span className={classes.fieldLabel}>
                                            {t('publications:searchCriteria.publishedYear', 'Published Year')}
                                        </span>
                                        <StisFilterDropdown
                                            ariaLabel={t('publications:searchCriteria.publishedYear', 'Published Year')}
                                            placeholder={t('publications:searchCriteria.anyYear', 'Any year')}
                                            options={yearOptions.map((year) => ({ value: year, label: year }))}
                                            selected={current.publishedYear ? [current.publishedYear] : []}
                                            width="100%"
                                            disabled={datesLockedByMix}
                                            hint={datesLockedReason}
                                            onChange={(values) => emit({ publishedYear: values[0] })}
                                        />
                                    </div>

                                    <div className={classes.field}>
                                        <span className={classes.fieldLabel}>
                                            {t('publications:searchCriteria.publishedMonth', 'Published Month')}
                                        </span>
                                        <StisFilterDropdown
                                            ariaLabel={t(
                                                'publications:searchCriteria.publishedMonth',
                                                'Published Month'
                                            )}
                                            placeholder={t('publications:searchCriteria.anyMonth', 'Any month')}
                                            options={PUBLISHED_MONTHS.map((month) => ({
                                                value: month,
                                                label: formatPublishedMonth(month),
                                            }))}
                                            selected={current.publishedMonth ? [current.publishedMonth] : []}
                                            width="100%"
                                            disabled={datesLockedByMix}
                                            hint={datesLockedReason}
                                            onChange={(values) => emit({ publishedMonth: values[0] })}
                                        />
                                    </div>
                                </div>

                                {!showVehicleFilters && sessionVin && (
                                    <div
                                        className={`${classes.vehicle} ${classes.swapPane} ${
                                            hideVehiclePanes ? classes.swapPaneHidden : ''
                                        }`}
                                        aria-hidden={hideVehiclePanes}
                                    >
                                        <span className={classes.vehicleHeading}>
                                            <Icon name="car-subaru" size={20} color="blueOne" />
                                            {t('publications:searchCriteria.vehicle', 'Vehicle')}
                                        </span>

                                        <div className={classes.field}>
                                            <label htmlFor="stis-criteria-session-vin" className={classes.fieldLabel}>
                                                {t('publications:searchCriteria.vin', 'VIN')}
                                            </label>
                                            <div className={classes.vinRow}>
                                                <input
                                                    id="stis-criteria-session-vin"
                                                    className={`${classes.vinInput} ${classes.vinInputDisabled}`}
                                                    value={sessionVin}
                                                    readOnly
                                                    disabled
                                                />
                                            </div>
                                            <span className={classes.vinNote}>
                                                {t(
                                                    'publications:searchCriteria.vinFromSession',
                                                    'Every search in this session is scoped to the above VIN.'
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {showVehicleFilters && (
                                    <div
                                        className={`${classes.vehicle} ${classes.swapPane} ${
                                            hideVehiclePanes ? classes.swapPaneHidden : ''
                                        }`}
                                        aria-hidden={hideVehiclePanes}
                                    >
                                        <span className={classes.vehicleHeading}>
                                            <Icon name="car-subaru" size={20} color="blueOne" />
                                            {t('publications:searchCriteria.vehicle', 'Vehicle')}
                                        </span>

                                        <div className={classes.field}>
                                            <span className={classes.fieldLabel}>
                                                {t('publications:quickFilters.modelYear', 'Model Year')}
                                            </span>
                                            <StisFilterDropdown
                                                ariaLabel={t('publications:quickFilters.modelYear', 'Model Year')}
                                                placeholder={
                                                    hasVin
                                                        ? t(
                                                              'publications:searchCriteria.clearVinFirst',
                                                              'Clear the VIN to pick a vehicle'
                                                          )
                                                        : t(
                                                              'publications:searchCriteria.selectModelYear',
                                                              'Select model year'
                                                          )
                                                }
                                                options={modelYears.map((year) => ({ value: year, label: year }))}
                                                selected={current.modelYear ? [current.modelYear] : []}
                                                width="100%"
                                                disabled={hasVin}
                                                onChange={(values) => changeModelYear(values[0])}
                                            />
                                        </div>

                                        <div className={classes.field}>
                                            <span className={classes.fieldLabel}>
                                                {t('publications:quickFilters.carLine', 'Carline')}
                                            </span>
                                            <StisFilterDropdown
                                                ariaLabel={t('publications:quickFilters.carLine', 'Carline')}
                                                placeholder={t(
                                                    'publications:searchCriteria.selectCarline',
                                                    'Select carline'
                                                )}
                                                options={carlines.map((option) => ({
                                                    value: option.id,
                                                    label: option.description,
                                                }))}
                                                selected={selectedCarlineId ? [selectedCarlineId] : []}
                                                searchable
                                                width="100%"
                                                disabled={hasVin || needsYearFirst}
                                                onChange={(values) => changeCarline(values[0])}
                                            />
                                        </div>

                                        {supportsTrim && (
                                            <div className={classes.field}>
                                                <span className={classes.fieldLabel}>
                                                    {t('publications:searchCriteria.trim', 'Trim')}
                                                </span>
                                                <StisFilterDropdown
                                                    ariaLabel={t('publications:searchCriteria.trim', 'Trim')}
                                                    placeholder={t(
                                                        'publications:searchCriteria.selectTrim',
                                                        'Select trim'
                                                    )}
                                                    options={trims.map((option) => ({
                                                        value: option.vehicleId,
                                                        label: option.label,
                                                    }))}
                                                    selected={current.trim ? [current.trim.vehicleId] : []}
                                                    width="100%"
                                                    disabled={hasVin || !selectedCarlineId}
                                                    onChange={(values) => changeTrim(values[0])}
                                                />
                                            </div>
                                        )}

                                        {isSignedIn && (
                                            <>
                                                <div className={classes.divider}>
                                                    {t('publications:searchCriteria.or', 'OR')}
                                                </div>

                                                <div className={classes.field}>
                                                    <label htmlFor="stis-criteria-vin" className={classes.fieldLabel}>
                                                        {t('publications:searchCriteria.vin', 'VIN')}
                                                    </label>
                                                    <div className={classes.vinRow}>
                                                        <input
                                                            id="stis-criteria-vin"
                                                            className={`${classes.vinInput} ${
                                                                classes.vinInputDisabled
                                                            } ${vinError ? classes.vinInputInvalid : ''}`}
                                                            value={vinText}
                                                            maxLength={24}
                                                            disabled={vinDisabled}
                                                            placeholder={
                                                                vinDisabled
                                                                    ? t(
                                                                          'publications:searchCriteria.vinDisabled',
                                                                          'Clear the vehicle to search by VIN'
                                                                      )
                                                                    : t(
                                                                          'publications:searchCriteria.vinPlaceholder',
                                                                          'Enter the full 17-character VIN'
                                                                      )
                                                            }
                                                            aria-invalid={Boolean(vinError)}
                                                            aria-describedby={
                                                                vinError ? 'stis-criteria-vin-error' : undefined
                                                            }
                                                            onChange={(event) =>
                                                                emit({ vin: event.target.value || undefined })
                                                            }
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    onSubmit();
                                                                }
                                                            }}
                                                        />
                                                        {hasVin && (
                                                            <button
                                                                type="button"
                                                                className={classes.vinClear}
                                                                aria-label={t(
                                                                    'publications:searchCriteria.clearVin',
                                                                    'Clear the VIN'
                                                                )}
                                                                onClick={clearVin}
                                                            >
                                                                <Icon name="x-close" size={16} color="grayFive" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {vinError && (
                                                        <span
                                                            id="stis-criteria-vin-error"
                                                            role="alert"
                                                            className={classes.vinError}
                                                        >
                                                            {vinErrorMessage[vinError]}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={classes.header}>
                {collapsed ? (
                    <>
                        <button
                            type="button"
                            className={classes.filtersButton}
                            aria-expanded={false}
                            aria-controls={bodyId}
                            onClick={() => onCollapsedChange(false)}
                        >
                            <Icon name="filter-list" size={18} color="blueOne" />
                            {t('publications:searchCriteria.expandFilters', 'Expand filters')}
                            {appliedCount > 0 && <span className={classes.filtersCount}>{appliedCount}</span>}
                            <Icon name="chevron-down" size={16} color="blueOne" />
                        </button>

                        {chips.length > 0 ? (
                            <>
                                <span className={classes.barDivider} />
                                {chips.map((chip) => (
                                    <span
                                        key={chip.key}
                                        className={classes.chipWrap}
                                        onMouseEnter={chip.extra ? openTypeList : undefined}
                                        onMouseLeave={chip.extra ? closeTypeListSoon : undefined}
                                    >
                                        <span className={classes.chip} title={chip.extra ? undefined : chip.label}>
                                            <span className={classes.chipGroup}>{chipGroupLabel[chip.group]}</span>
                                            <span className={classes.chipLabel}>{chip.label}</span>
                                            {chip.extra ? (
                                                <button
                                                    type="button"
                                                    className={classes.chipExtra}
                                                    aria-expanded={isTypeListOpen}
                                                    aria-label={typeListLabel}
                                                    onClick={() =>
                                                        isTypeListOpen ? closeTypeListSoon() : openTypeList()
                                                    }
                                                    onFocus={openTypeList}
                                                >
                                                    +{chip.extra}
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className={classes.chipRemove}
                                                aria-label={
                                                    chip.extra
                                                        ? t(
                                                              'publications:searchCriteria.removeChipGroup',
                                                              'Remove {{label}} and {{count}} more',
                                                              { label: chip.label, count: chip.extra }
                                                          )
                                                        : t(
                                                              'publications:searchCriteria.removeChip',
                                                              'Remove {{label}}',
                                                              {
                                                                  label: chip.label,
                                                              }
                                                          )
                                                }
                                                onClick={() => removeChip(chip)}
                                            >
                                                <Icon name="x-close" size={14} color="blueOneActive" />
                                            </button>
                                        </span>

                                        {Boolean(chip.extra) && isTypeListOpen && (
                                            <div className={classes.typeList} role="group" aria-label={typeListLabel}>
                                                <div className={classes.typeListInner}>
                                                    {applied.publicationTypes.map((type) => (
                                                        <div key={type.publicationType} className={classes.typeListRow}>
                                                            <span
                                                                className={classes.typeListName}
                                                                title={type.publicationType}
                                                            >
                                                                {type.publicationType}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className={classes.chipRemove}
                                                                aria-label={t(
                                                                    'publications:searchCriteria.removeChip',
                                                                    'Remove {{label}}',
                                                                    { label: type.publicationType }
                                                                )}
                                                                onClick={() => removeType(type.publicationType)}
                                                            >
                                                                <Icon name="x-close" size={14} color="blueOneActive" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </span>
                                ))}
                            </>
                        ) : (
                            <span className={classes.barEmpty}>
                                {t('publications:searchCriteria.noFilters', 'No filters applied')}
                            </span>
                        )}

                        {resultCount !== undefined && (
                            <span className={classes.barCount}>
                                <strong>{resultCount.toLocaleString()}</strong>{' '}
                                {t('publications:searchCriteria.publications', 'publications')}
                            </span>
                        )}
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className={classes.headerActionStart}
                            aria-expanded={true}
                            aria-controls={bodyId}
                            onClick={onCollapse}
                        >
                            {t('publications:searchCriteria.collapse', 'Collapse')}
                            <Icon name="chevron-up" size={16} color="blueOne" />
                        </button>
                        {resultCount !== undefined && (
                            <span className={classes.barCount}>
                                <strong>{resultCount.toLocaleString()}</strong>{' '}
                                {t('publications:searchCriteria.publications', 'publications')}
                            </span>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};
